import { forwardRef, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Modal, ModalHandle } from "@src/components/Modal/Modal";
import { Button } from "@src/components/Button/Button";
import { ButtonVariant } from "@src/components/Button/Button.constants";
import { DatePicker } from "@src/components/DatePicker/DatePicker";
import { SelectYup } from "@src/components/Select/SelectYup";
import Loader from "@src/components/Loader/Loader";
import { getMarketplaceDisplayName } from "@src/constants/marketplaceEventUrls";
import { OptionType } from "@src/utils/reactSelectUtils";
import { PipelineHealthSummary, PipelineJobType } from "./pipelineHealth.types";
import { PipelineHealthTableRow } from "./PipelineHealthTableRow";
import {
	NO_RUNS_STATE,
	formatDateTime,
	getJobTypeLabel,
	getPipelineHealthRowKey,
	getRowStateKey,
	getTodayRangeValue,
	parseDateRangeValue,
	sortPipelineHealthRows,
	toIsoRangeBounds,
} from "./pipelineHealthUtils";
import { FiltersRow, GeneratedAt, StateMessage, Table, TableHeaderRow } from "./PipelineHealthPopup.styled";

interface PipelineHealthPopupProps {
	summary: PipelineHealthSummary | null;
	loading: boolean;
	error: boolean;
	refreshing: boolean;
	onRefresh: () => void;
}

interface FiltersFormValues {
	dateRange: string;
	jobType: string;
	marketplace: string;
	state: string;
}

const JOB_TYPE_OPTIONS: Array<PipelineJobType> = ["collection", "automapper", "reprocessing"];

const STATE_OPTIONS: Array<OptionType> = [
	{ value: "in_progress", label: "In progress" },
	{ value: "completed", label: "Completed" },
	{ value: "failed", label: "Failed" },
	{ value: NO_RUNS_STATE, label: "No runs" },
];

export const PipelineHealthPopup = forwardRef<ModalHandle, PipelineHealthPopupProps>(
	({ summary, loading, error, refreshing, onRefresh }, ref) => {
		const { control } = useForm<FiltersFormValues>({
			defaultValues: { dateRange: getTodayRangeValue(), jobType: "", marketplace: "", state: "" },
		});
		const dateRangeValue = useWatch({ control, name: "dateRange" });
		const jobTypeFilter = useWatch({ control, name: "jobType" });
		const marketplaceFilter = useWatch({ control, name: "marketplace" });
		const stateFilter = useWatch({ control, name: "state" });
		const [expandedKey, setExpandedKey] = useState<string | null>(null);

		const rows = useMemo(() => summary?.rows ?? [], [summary]);

		const jobTypeOptions = useMemo<Array<OptionType>>(
			() => JOB_TYPE_OPTIONS.map((jobType) => ({ value: jobType, label: getJobTypeLabel(jobType) })),
			[],
		);

		const marketplaceOptions = useMemo<Array<OptionType>>(() => {
			const distinct = Array.from(new Set(rows.map((row) => row.marketplace).filter((marketplace): marketplace is string => !!marketplace)));
			return distinct
				.sort((a, b) => getMarketplaceDisplayName(a).localeCompare(getMarketplaceDisplayName(b)))
				.map((marketplace) => ({ value: marketplace, label: getMarketplaceDisplayName(marketplace) }));
		}, [rows]);

		const visibleRows = useMemo(() => {
			const filtered = rows.filter((row) => {
				if (jobTypeFilter && row.jobType !== jobTypeFilter) return false;
				if (marketplaceFilter && row.marketplace != null && row.marketplace !== marketplaceFilter) return false;
				if (stateFilter && getRowStateKey(row) !== stateFilter) return false;
				return true;
			});
			return sortPipelineHealthRows(filtered);
		}, [rows, jobTypeFilter, marketplaceFilter, stateFilter]);

		const range = useMemo(() => {
			const parsed = parseDateRangeValue(dateRangeValue);
			return parsed ? toIsoRangeBounds(parsed) : null;
		}, [dateRangeValue]);

		const handleClose = () => {
			setExpandedKey(null);
		};

		return (
			<Modal ref={ref} title="Pipeline Health" hasFooter={false} onClose={handleClose} maxWidth="1150px">
				<FiltersRow>
					<DatePicker
						control={control}
						name="dateRange"
						variant="range"
						placeholder="Date range"
						wrapperStyle={{ width: "185px", flexShrink: 0 }}
						wrapperClassName="pipeline-health-date-picker"
					/>
					<SelectYup control={control} name="jobType" placeholder="Job type" options={jobTypeOptions} />
					<SelectYup control={control} name="marketplace" placeholder="Marketplace" options={marketplaceOptions} />
					<SelectYup control={control} name="state" placeholder="State" options={STATE_OPTIONS} />
					<Button
						variant={ButtonVariant.Tertiary}
						controlSize="sm"
						onClick={onRefresh}
						isLoading={refreshing}
						style={{ height: "3.2rem" }}>
						Refresh
					</Button>
					{summary && <GeneratedAt>Updated {formatDateTime(summary.generatedAt)}</GeneratedAt>}
				</FiltersRow>

				{loading && !summary && (
					<StateMessage>
						<Loader controlSize="xl" />
					</StateMessage>
				)}
				{!loading && error && <StateMessage>Pipeline health could not be loaded. Please try again.</StateMessage>}
				{!loading && !error && visibleRows.length === 0 && <StateMessage>No pipeline jobs match the current filters.</StateMessage>}

				{!error && visibleRows.length > 0 && (
					<Table>
						<TableHeaderRow>
							<span />
							<span>Job</span>
							<span>Marketplace</span>
							<span>State</span>
							<span>Started</span>
							<span>Finished</span>
							<span>Duration</span>
						</TableHeaderRow>
						{visibleRows.map((row) => {
							const key = getPipelineHealthRowKey(row);
							return (
								<PipelineHealthTableRow
									key={key}
									row={row}
									isExpanded={expandedKey === key}
									onToggle={() => setExpandedKey((prev) => (prev === key ? null : key))}
									range={range}
								/>
							);
						})}
					</Table>
				)}
			</Modal>
		);
	},
);

PipelineHealthPopup.displayName = "PipelineHealthPopup";
